import PageContainer from "../PageContainer";
import { useData } from "../../../contexts/DataContext/DataContextt";
import { FeatureForm } from "./FeatureForm";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { editFeature, fetchFeatures } from "../../../services/featureService";
import { usePreload } from "../../../contexts/DataContext/PreloadContext";
import { featuresPageCache } from "../../../pages/cache/PageCache";
import { usePagedQuery } from "../../../contexts/hooks/usePagedQuery";
import { Stack, Tooltip, IconButton, Chip } from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { Link, Navigate, useParams } from "react-router-dom";
import RefreshIcon from "@mui/icons-material/Refresh";
import { localizeField } from "../../../config/localization";
import { fetchFeatureClassRelations } from "../../../services/includedFeatureService";

const EditFeature = () => {
  const { id: featureId } = useParams();
  const { loading, language } = useData();
  const [updating, setUpdating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [formVersion, setFormVersion] = useState(0);
  const { showNotification } = usePreload();
  // FeatureForm publishes its unsaved-changes confirm here so Refresh can use it
  const leaveGuardRef = useRef(null);
  const requestRef = useRef(0);
  const [relations, setRelations] = useState({ status: "loading", data: [] });
  const loadRelations = useCallback(async () => {
    const request = ++requestRef.current;
    setRelations((previous) => ({ ...previous, status: "loading" }));
    try {
      const data = await fetchFeatureClassRelations(featureId, language);
      if (request === requestRef.current) setRelations({ status: "ready", data });
      return request === requestRef.current;
    } catch (error) {
      console.error(error);
      if (request === requestRef.current) setRelations((previous) => ({ ...previous, status: "error" }));
      return false;
    }
  }, [featureId, language]);

  useEffect(() => {
    if (featureId) loadRelations();
    return () => { requestRef.current++; };
  }, [featureId, loadRelations]);

  const {
    mutate,
    addOrDeleteIncludedFeatures,
    reload,
    fetched,
    data: rows,
  } = usePagedQuery({
    fetchFunction: fetchFeatures,
    searchOption: [{ id: "featureId", display: "Code" }],
    cache: featuresPageCache,
    language: "all",
    searchKey: featureId ?? "",
    deps: [language],
  });

  const row = useMemo(() => {
    return {
      ...rows[0],
      featureName: localizeField(rows, "featureName"),
      featureDisplayName: localizeField(rows, "featureDisplayName"),
      featureDescription: localizeField(rows, "featureDescription"),
      featureTooltip: localizeField(rows, "featureTooltip"),
    };
  }, [rows]);

  if (!featureId || (fetched && !row?.featureId)) {
    return <Navigate to="/notfound" replace />;
  }

  const onUpdate = async (updatedRow) => {
    if (refreshing || relations.status !== "ready") return { success: false };
    let includedClasses = updatedRow.includedClasses;
    try {
      setUpdating(true);
      //let newFeature = keepOnlyCurrentLanguage(updatedRow, language);
      let newFeature = {
        ...updatedRow,
        featureIncludedInClassCodes: updatedRow.includedClasses
          ?.filter((f) => !f.deleted)
          .map((m) => m.id)
          .join("|"),
      };

      // Save|Delete included features
      includedClasses = await addOrDeleteIncludedFeatures(newFeature);
      newFeature = { ...newFeature, includedClasses: includedClasses };

      // Save feature
      // apiRequest hands back the raw Response on a non-2xx instead of throwing
      const response = await editFeature(newFeature);
      if (response instanceof Response && !response.ok) {
        throw new Error(`Edit feature failed with status ${response.status}`);
      }
      mutate(
        {
          ...newFeature,
          id: newFeature.featureId,
          featureName: newFeature.featureName[language] ?? "",
          featureDisplayName: newFeature.featureDisplayName[language] ?? "",
          featureDescription: newFeature.featureDescription[language] ?? "",
          featureToolTip: newFeature.featureToolTip[language] ?? "",
        },
        "featureId",
      );
      setRelations({ status: "ready", data: includedClasses });

      showNotification(`${updatedRow?.featureId} was updated!`, "success");
      return { success: true, feature: newFeature };
    } catch (e) {
      console.error(e);
      showNotification(
        `something went wrong updating the ${updatedRow?.featureId} feature.`,
        "error",
      );
      return {
        success: false,
        includedClasses: e.includedClasses ?? includedClasses,
      };
    } finally {
      setUpdating(false);
    }
  };

  const handleRefresh = async () => {
    // no guard registered yet means there is nothing to confirm
    if (leaveGuardRef.current && !(await leaveGuardRef.current())) return;
    setRefreshing(true);
    try {
      const [updated, relationsLoaded] = await Promise.all([reload(true), loadRelations()]);
      if (updated) mutate(updated, "featureId");
      if (relationsLoaded) setFormVersion((version) => version + 1);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <PageContainer
      title="Edit Feature"
      subtitle="Update properties, localized texts and class relations"
      titleAdornment={
        <Stack direction="row" spacing={1}>
          <Chip size="small" label={featureId} />
          {row?.featureType && (
            <Chip size="small" variant="outlined" label={row.featureType} />
          )}
        </Stack>
      }
      breadcrumbs={[
        { title: "ETIM Feature", path: "/featuresnew" },
        { title: featureId },
      ]}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Refresh feature" placement="bottom" enterDelay={300}>
            <div>
              <IconButton
                size="small"
                aria-label="Refresh feature"
                onClick={handleRefresh}
              >
                <RefreshIcon />
              </IconButton>
            </div>
          </Tooltip>
          <Tooltip title="Back" placement="bottom" enterDelay={300}>
            <div>
              {/* a Link so FeatureForm's unsaved-changes guard covers it */}
              <IconButton
                size="small"
                aria-label="Back to features"
                component={Link}
                to="/featuresnew"
              >
                <KeyboardBackspaceIcon />
              </IconButton>
            </div>
          </Tooltip>
        </Stack>
      }
    >
      <FeatureForm
        key={`${featureId}-${formVersion}`}
        onSave={onUpdate}
        props={{
          row: row,
          loading: loading,
          uploading: updating,
          showCancel: false,
          saveText: "Save Changes",
          isUpdate: true,
          leaveGuardRef,
          relations,
          refreshing,
          retryRelations: loadRelations,
        }}
      />
    </PageContainer>
  );
};

export default EditFeature;
